import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GUILDS } from "../../data/seed";
import { isRegionUnlocked } from "../../lib/progression";
import { usePortal } from "../../state/PortalContext";
import type { Region } from "../../types/domain";
import { Icon } from "../../components/Icon";
import { WorldMapCanvas } from "../../components/WorldMapCanvas";
import { Button, Card, Pill, ProgressBar, SectionHeading } from "../../components/ui";

export function WorldMapPage() {
  const { curriculum, progress, user, switchGuild } = usePortal();
  const initialRegion =
    curriculum.regions.find((region) => isRegionUnlocked(region, progress)) ??
    curriculum.regions[0];
  const [selected, setSelected] = useState<Region>(initialRegion);

  const regionQuests = useMemo(
    () =>
      curriculum.quests.filter((quest) => quest.regionId === selected.id),
    [curriculum.quests, selected.id],
  );
  const liveQuest = regionQuests.find(
    (quest) => quest.templateId !== "coming-soon",
  );
  const unlocked = isRegionUnlocked(selected, progress);

  if (!user) return null;

  return (
    <div className="space-y-7">
      <SectionHeading
        eyebrow="The Kingdom of Game Mathematics"
        title="Choose your next destination"
        detail="Every route tells a different story, but your mastery is shared across all five guild paths."
        action={
          <div className="flex max-w-full gap-1 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-950/65 p-1.5">
            {GUILDS.map((guild) => (
              <button
                key={guild.id}
                onClick={() => void switchGuild(guild.id)}
                className={`min-h-10 whitespace-nowrap rounded-xl px-3 text-xs font-extrabold transition ${
                  user.guild === guild.id
                    ? "bg-indigo-500 text-white"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
                title={guild.description}
              >
                {guild.shortName}
              </button>
            ))}
          </div>
        }
      />

      <WorldMapCanvas
        curriculum={curriculum}
        progress={progress}
        guild={user.guild}
        selectedRegionId={selected.id}
        onSelect={setSelected}
      />

      <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <Card className="p-6 sm:p-7">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div
              className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl border-2 bg-slate-950"
              style={{ borderColor: selected.color }}
            >
              <Icon name={selected.icon} size={28} style={{ color: selected.color }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone={unlocked ? "emerald" : "slate"}>
                  {unlocked ? "Region unlocked" : "Complete the prior route"}
                </Pill>
                <Pill tone="amber">{selected.xpAvailable} XP available</Pill>
              </div>
              <h2 className="mt-3 font-display text-3xl font-bold text-white">
                {selected.name}
              </h2>
              <p className="mt-1 text-sm font-bold" style={{ color: selected.color }}>
                {selected.topic}
              </p>
              <p className="mt-3 leading-7 text-slate-400">
                {selected.description}
              </p>
              <div className="mt-5">
                <ProgressBar
                  value={progress.regionCompletion[selected.id] ?? 0}
                  label="Region completion"
                />
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3 border-t border-slate-800 pt-6 sm:flex-row">
            {liveQuest && unlocked ? (
              <Link to={`/app/quest/${liveQuest.id}`}>
                <Button className="w-full sm:w-auto">
                  <Icon name="Play" size={18} />
                  Begin recommended quest
                </Button>
              </Link>
            ) : (
              <Button disabled>
                <Icon name="LockKeyhole" size={18} />
                {unlocked ? "Expedition coming soon" : "Region locked"}
              </Button>
            )}
            <Link to="/app/dashboard">
              <Button variant="secondary" className="w-full sm:w-auto">
                Open skill codex
              </Button>
            </Link>
          </div>
        </Card>

        <Card className="p-6 sm:p-7">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-retro text-[8px] tracking-widest text-cyan-300">
                LOCAL QUEST LOG
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold text-white">
                {selected.subAreas.length} sub-areas
              </h3>
            </div>
            <Icon name="ScrollText" size={27} className="text-slate-600" />
          </div>
          <div className="mt-5 space-y-3">
            {selected.subAreas.map((area) => {
              const quest = regionQuests.find(
                (candidate) => candidate.subAreaId === area.id,
              );
              const complete = quest
                ? progress.completedQuestIds.includes(quest.id)
                : false;
              return (
                <div
                  key={area.id}
                  className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-950/55 p-4"
                >
                  <span
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
                      complete
                        ? "bg-emerald-400/15 text-emerald-300"
                        : quest?.templateId !== "coming-soon"
                          ? "bg-indigo-400/15 text-indigo-300"
                          : "bg-slate-800 text-slate-500"
                    }`}
                  >
                    <Icon
                      name={
                        complete
                          ? "Check"
                          : quest?.templateId !== "coming-soon"
                            ? "Play"
                            : "LockKeyhole"
                      }
                      size={17}
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-200">{area.name}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {area.summary}
                    </p>
                  </div>
                  <Pill
                    tone={
                      complete
                        ? "emerald"
                        : quest?.templateId !== "coming-soon"
                          ? "indigo"
                          : "slate"
                    }
                  >
                    {complete
                      ? "Complete"
                      : quest?.templateId !== "coming-soon"
                        ? "Ready"
                        : "Soon"}
                  </Pill>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-orange-300 to-rose-500 text-slate-950">
          <Icon name="BrainCircuit" size={27} />
        </div>
        <div className="flex-1">
          <p className="font-display text-lg font-bold text-white">
            Professor's field note
          </p>
          <p className="mt-1 text-sm leading-6 text-slate-400">
            “Your guild changes the suggested trail, not what you have learned.
            Follow curiosity, and switch paths whenever another kind of game
            system calls to you.”
          </p>
        </div>
      </Card>
    </div>
  );
}
